import {
  Button,
  Buttons,
  CancelButton,
  CloudFolderLink,
  DeleteButton,
  Drawer,
  Field,
  Form,
  FormMessage,
  Icon,
  OpportunityTagMultiSelect,
  Tooltip,
} from '~/components';
import { useApi, useConfirmation, useMember, useWorkspace } from '~/contexts';
import { Formik } from 'formik';
import { useCurrencyFormat, useDirtyCheck, useDocumentTitle, useFeatures, useForm } from '~/hooks';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ErrorPage } from '~/routes/public/pages';
import styled from 'styled-components';
import { colors } from '~/styles';
import { emptyStringToNull, mergeValues } from '~/utils';
import * as Yup from 'yup';
import OpportunityDeleteConfirmation from './OpportunityDeleteConfirmation';

const CompanySelectWrapper = styled.div`
  flex-grow: 1;
`;

const ViewCompanyIcon = styled(Icon)`
  left: calc(100% + 1rem);
  position: absolute;
  top: 1.5rem;
  color: ${colors.grey25};
  &:hover {
    color: ${colors.grey55};
    cursor: pointer;
  }
`;

const ProbabilityWarningIcon = styled(Icon)`
  left: calc(100% + 1rem);
  position: absolute;
  top: 1.5rem;
`;

function OpportunityForm({ onSaved, onDeleted, onClose }) {
  useDocumentTitle('Edit Opportunity');

  const api = useApi();
  const { workspace } = useWorkspace();
  const { member } = useMember();
  const features = useFeatures();
  const [query, setQuery] = useState({ isReady: false, data: null });
  const [{ status, message, isSubmitting }, form] = useForm();

  const { opportunityId, opportunityTab } = useParams();
  const history = useHistory();

  const confirmation = useConfirmation();

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).opportunities(opportunityId).get();
      setQuery({ isReady: true, data });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [workspace.id, opportunityId, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function handleClose() {
    if (onClose) {
      onClose(opportunityTab);
    }
  }

  const opportunity = query.data;
  const salesforceReadOnly = !!opportunity?.salesforceOpportunity?.salesforceId;
  const hubspotReadOnly = !!opportunity?.hubspotOpportunity?.hubspotId;
  const currencyFormat = useCurrencyFormat({
    currency: opportunity?.currency,
  });

  const handleDelete = () => {
    confirmation.prompt((resolve) => (
      <OpportunityDeleteConfirmation
        opportunity={opportunity}
        onClose={resolve}
        onDelete={() => {
          history.push(`/app/${workspace.key}/pipeline/opportunities`);
          if (onDeleted) onDeleted(opportunity.id);
          resolve(true);
        }}
      />
    ));
  };

  if (!query.isReady) return null;
  if (!opportunity) return <ErrorPage.NotFound />;

  const initialValues = mergeValues(
    {
      amount: '',
      openDate: null,
      closeDate: null,
      company: null,
      currency: workspace.currency,
      cloudFolderUrl: '',
      description: '',
      name: '',
      nextSteps: '',
      opportunityLeadSource: null,
      opportunityType: null,
      tags: [],
      owner: null,
      opportunityStage: null,
      opportunityPipeline: null,
      practice: null,
      priorityId: '',
      probability: '',
    },
    opportunity,
  );

  return (
    <Drawer
      isOpen
      title="Edit Opportunity"
      byline={opportunity.name}
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={handleClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values) {
          try {
            form.submit();
            const body = emptyStringToNull({
              ..._.omit(values, [
                'company',
                'opportunityLeadSource',
                'opportunityType',
                'tags',
                'owner',
                'opportunityStage',
                'opportunityPipeline',
                'practice',
              ]),
              companyId: values.company?.id ?? null,
              opportunityLeadSourceId: values.opportunityLeadSource?.id ?? null,
              opportunityTypeId: values.opportunityType?.id ?? null,
              opportunityTagAssignments: values.tags.map((tag) => ({ opportunityTagId: tag.id })),
              ownerId: values.owner?.id ?? null,
              opportunityStageId: values.opportunityStage?.id ?? null,
              opportunityPipelineId: values.opportunityPipeline?.id ?? null,
              practiceId: values.practice?.id ?? null,
            });

            const { data } = await api.www.workspaces(workspace.id).opportunities(opportunity?.id).upsert(body);

            const isWon =
              !_.isEqual(values.opportunityStage, initialValues.opportunityStage) &&
              values.opportunityStage?.statusId === 'won';

            await onSaved(data, isWon);
            closeDrawer();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape(
              hubspotReadOnly
                ? {
                    practice: Yup.object().label('Practice').nullable(),
                    cloudFolderUrl: Yup.string()
                      .label('Cloud Folder')
                      .url('Please include http:// or https:// protocols in your URL')
                      .max(1024),
                  }
                : salesforceReadOnly
                  ? {
                      practice: Yup.object().label('Practice').nullable(),
                      priorityId: Yup.string().label('Priority').oneOf(['low', 'medium', 'high']).nullable(),
                      cloudFolderUrl: Yup.string()
                        .label('Cloud Folder')
                        .url('Please include http:// or https:// protocols in your URL')
                        .max(1024),
                    }
                  : {
                      amount:
                        opportunity.totalProjectAmount > 0
                          ? Yup.number()
                              .label('Amount')
                              .required('Amount is required when related projects have amounts')
                              .min(
                                opportunity.totalProjectAmount,
                                `The opportunity amount cannot be less than the total sum of associated project amounts (${currencyFormat.format(opportunity.totalProjectAmount)})`,
                              )
                              .max(99999999999)
                          : Yup.number()
                              .label('Amount')
                              .min(0.01, 'Amount must be at least 0.01')
                              .max(99999999999)
                              .nullable(),
                      openDate: Yup.date().label('Open Date').required(),
                      closeDate: Yup.date().label('Expected Close Date').nullable(),
                      company: Yup.mixed().label('Company').required(),
                      currency: Yup.string().label('Currency').required(),
                      cloudFolderUrl: Yup.string()
                        .label('Cloud Folder')
                        .url('Please include http:// or https:// protocols in your URL')
                        .max(1024),
                      description: Yup.string().label('Description').max(5000),
                      name: Yup.string().label('Name').max(255).required(),
                      nextSteps: Yup.string().label('Next Steps').max(255).nullable(),
                      owner: Yup.object().label('Owner').nullable(),
                      opportunityStage: Yup.object().label('Stage').nullable().required(),
                      opportunityPipeline: Yup.object().label('Pipeline').nullable().required(),
                      practice: Yup.object().label('Practice').nullable(),
                      priorityId: Yup.string().label('Priority').oneOf(['low', 'medium', 'high']).nullable(),
                      probability: Yup.number()
                        .label('Probability')
                        .min(0)
                        .max(100)
                        .nullable()
                        .required('Probability must be between 0 and 100'),
                    },
            )}>
            {(formik) => {
              const { values, setValues, submitForm } = formik;

              const handleOpportunityStageChange = ({ target: { value } }) => {
                setValues({
                  ...values,
                  opportunityStage: value,
                  probability: value ? value.probability : values.probability,
                });
              };

              // Remove the stage when changing pipeline
              const handleOpportunityPipelineChange = ({ target: { value } }) => {
                setValues({
                  ...values,
                  opportunityPipeline: value,
                  opportunityStage: null,
                });
              };

              // Disable company change if we have projects, check later
              const handleCompanyChange = ({ target: { value } }) => {
                setValues({
                  ...values,
                  company: value,
                });
              };

              return (
                <Form>
                  {status && <FormMessage.Error spaceBottom>{message}</FormMessage.Error>}

                  <Form.Section title="Basic Information">
                    <Form.Control>
                      <Field.Text
                        name="name"
                        placeholder="Name"
                        maxLength={255}
                        disabled={salesforceReadOnly || hubspotReadOnly}
                      />
                    </Form.Control>

                    <Form.Control>
                      <div style={{ display: 'flex' }}>
                        <CompanySelectWrapper>
                          {opportunity.hasProjects ? (
                            <Field.Text
                              name="company"
                              placeholder="Company"
                              value={opportunity.company?.name}
                              disabled
                            />
                          ) : (
                            <Field.CompanySelect
                              name="company"
                              placeholder="Company"
                              allowNew
                              onChange={handleCompanyChange}
                              clearable={false}
                              disabled={salesforceReadOnly || hubspotReadOnly}
                            />
                          )}
                        </CompanySelectWrapper>

                        {values.company && (
                          <a
                            href={`/app/${workspace.key}/pipeline/companies/${values.company.id}/overview`}
                            target="_blank"
                            rel="noopener noreferrer">
                            <Tooltip message={'View Company'} placement="top">
                              <ViewCompanyIcon icon="external-link-alt" />
                            </Tooltip>
                          </a>
                        )}
                      </div>
                    </Form.Control>

                    <Form.Control>
                      <Field.MemberSelect
                        name="owner"
                        placeholder="Owner"
                        initialValue={opportunity?.owner}
                        disabled={salesforceReadOnly || hubspotReadOnly}
                        clearable={!salesforceReadOnly && !hubspotReadOnly}
                      />
                    </Form.Control>

                    {features.practices && (
                      <Form.Control>
                        <Field.PracticeSelect name="practice" placeholder="Practice" allowNew />
                      </Form.Control>
                    )}

                    <Form.Control>
                      <Field.OpportunityPipelineSelect
                        name="opportunityPipeline"
                        placeholder="Pipeline"
                        onChange={handleOpportunityPipelineChange}
                        disabled={salesforceReadOnly || hubspotReadOnly}
                      />
                    </Form.Control>

                    <Form.Control>
                      <Field.OpportunityStageSelect
                        name="opportunityStage"
                        placeholder="Stage"
                        clearable={false}
                        onChange={handleOpportunityStageChange}
                        opportunityPipelineId={values.opportunityPipeline?.id}
                        initialValue={opportunity?.opportunityStage}
                        disabled={salesforceReadOnly || hubspotReadOnly}
                      />
                    </Form.Control>

                    <Form.Control>
                      <div style={{ display: 'flex' }}>
                        <div style={{ flexGrow: 1 }}>
                          <Field.Number
                            name="probability"
                            placeholder="Probability to Close"
                            min={0}
                            max={100}
                            precision={2}
                            prefix="%"
                            locale={member.locale}
                            style={{ textAlign: 'left' }}
                            disabled={salesforceReadOnly || hubspotReadOnly}
                          />
                        </div>
                        {values.opportunityStage && values.probability !== values.opportunityStage.probability && (
                          <Tooltip message="The opportunity probability differs from the stage default probability.">
                            <ProbabilityWarningIcon icon="exclamation-triangle" color={colors.warning} />
                          </Tooltip>
                        )}
                      </div>
                    </Form.Control>

                    <Form.Control>
                      <Field.Currency
                        name="amount"
                        placeholder="Amount"
                        currency={values.currency}
                        disabled={salesforceReadOnly || hubspotReadOnly}
                      />
                      <Field.WorkspaceCurrencySelect
                        name="currency"
                        placeholder="Currency"
                        clearable={false}
                        disabled={
                          !features.multicurrency || salesforceReadOnly || hubspotReadOnly || opportunity.hasProjects
                        }
                      />
                    </Form.Control>

                    <Form.Control>
                      <Field.DayPicker
                        name="openDate"
                        placeholder="Open Date"
                        disabled={salesforceReadOnly || hubspotReadOnly}
                        clearable={false}
                      />
                      <Field.DayPicker
                        name="closeDate"
                        placeholder="Expected Close Date"
                        disabled={salesforceReadOnly || hubspotReadOnly}
                        clearable={!salesforceReadOnly && !hubspotReadOnly}
                      />
                    </Form.Control>

                    <Form.Control>
                      <Field.TextArea
                        name="nextSteps"
                        placeholder="Next Steps"
                        maxLength={255}
                        rows={3}
                        disabled={salesforceReadOnly || hubspotReadOnly}
                      />
                    </Form.Control>

                    <Form.Control>
                      <Field.OpportunityPrioritySelect
                        name="priorityId"
                        placeholder="Priority"
                        showEmptyOption={!hubspotReadOnly}
                        disabled={hubspotReadOnly}
                      />
                    </Form.Control>

                    <Form.Control>
                      <Field.OpportunityTypeSelect
                        name="opportunityType"
                        placeholder="Opportunity Type"
                        allowNew
                        initialValue={opportunity?.opportunityType}
                        disabled={salesforceReadOnly || hubspotReadOnly}
                        clearable={!salesforceReadOnly && !hubspotReadOnly}
                      />
                    </Form.Control>

                    <Form.Control>
                      <OpportunityTagMultiSelect
                        name="tags"
                        placeholder="Tags"
                        value={formik.values.tags}
                        allowNew
                        onChange={({ target: { value } }) => formik.setFieldValue('tags', value)}
                      />
                    </Form.Control>

                    <Form.Control>
                      <Field.OpportunityLeadSourceSelect
                        name="opportunityLeadSource"
                        placeholder="Opportunity Lead Source"
                        allowNew
                        initialValue={opportunity?.opportunityLeadSource}
                        disabled={salesforceReadOnly || hubspotReadOnly}
                        clearable={!salesforceReadOnly && !hubspotReadOnly}
                      />
                    </Form.Control>

                    <Form.Control>
                      <Field.TextArea
                        name="description"
                        placeholder="Description"
                        maxLength={5000}
                        disabled={salesforceReadOnly || hubspotReadOnly}
                      />
                    </Form.Control>
                  </Form.Section>
                  <Form.Section
                    title="Cloud Folder"
                    help="Assign a shared cloud folder to this opportunity to let team members view files.">
                    <Form.Control>
                      <div>
                        <Field.Text name="cloudFolderUrl" placeholder="Cloud Folder URL" maxLength={1024} />
                      </div>

                      {!!formik.values.cloudFolderUrl &&
                        Yup.string().url().isValidSync(formik.values.cloudFolderUrl) && (
                          <div style={{ flex: '0 0 3rem', alignSelf: 'center' }}>
                            <CloudFolderLink url={formik.values.cloudFolderUrl} />
                          </div>
                        )}
                    </Form.Control>
                  </Form.Section>

                  <Drawer.Actions>
                    {opportunity.id && opportunity.permissions.manage && !opportunity.hasProjects && (
                      <DeleteButton onClick={handleDelete}>Delete</DeleteButton>
                    )}

                    <Buttons align="right">
                      <Buttons align="right">
                        <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                        <Button isLoading={isSubmitting} onClick={submitForm}>
                          Save &amp; Close
                        </Button>
                      </Buttons>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}

export default OpportunityForm;
